import React, { useEffect, useState } from 'react';
import { Button, Badge, FadeIn } from '../components/ui';
import { rupee } from '../theme';
import { useAuth } from '../context/AuthContext';
import { priceFor, serviceChargeFor } from './Packages';
import api from '../api/client';
import { payForBooking } from '../api/payment';

const STEPS = ['Address', 'Schedule', 'Review'];
const defaultSlots = ['Morning (8–11 AM)', 'Afternoon (12–3 PM)', 'Evening (4–7 PM)'];

export default function BookingFlow({ pkg, onClose }) {
  const { selectedLocation, user } = useAuth();
  // City is auto-selected from the home-page location (editable here).
  const [addr, setAddr] = useState({
    house: '', line1: '',
    city: selectedLocation?.name || selectedLocation?.city || '',
    pincode: selectedLocation?.pincode || '',
  });
  const [slots, setSlots] = useState(defaultSlots);
  const [autoAssign, setAutoAssign] = useState(false);
  const [slot, setSlot] = useState(defaultSlots[0]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [appointmentFee, setAppointmentFee] = useState(0);
  // Always show a package-details screen first after Book Now.
  const [intro, setIntro] = useState(true);

  // Admin-managed time slots / auto-assign + appointment fee.
  useEffect(() => {
    let alive = true;
    api.get('/settings/public').then((d) => {
      if (!alive) return;
      if (d?.timeSlots?.length) { setSlots(d.timeSlots); setSlot(d.timeSlots[0]); }
      if (d?.autoAssignSlot) setAutoAssign(true);
      if (d?.appointmentFee != null) setAppointmentFee(d.appointmentFee);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const pr = priceFor(pkg, selectedLocation?.name);
  const price = pr.discountPrice || pr.price;
  const canNext = step === 0 ? addr.house && addr.city && addr.pincode.length === 6 : true;

  // Booking starts by scheduling the consultation call — the client pays the
  // appointment fee. Interview, security and trial follow from "My Bookings".
  const placeAndPay = async () => {
    setLoading(true);
    let bookingId = 'demo';
    const chosenSlot = autoAssign ? 'Auto-assigned by admin' : slot;
    try {
      await api.post('/users/me/addresses', {
        line1: [addr.house, addr.line1].filter(Boolean).join(', '),
        city: addr.city,
        pincode: addr.pincode,
      }).catch(() => {});
      const data = await api.post('/bookings', { packageId: pkg._id, notes: chosenSlot });
      bookingId = data.booking._id;
    } catch (e) {
      /* offline/demo — continue with a mock booking */
    }
    const pay = await payForBooking(bookingId, {
      name: `${pkg.title} — Appointment`,
      prefill: { name: user?.name, mobile: user?.mobile, email: user?.email },
    });
    if (pay.success && bookingId !== 'demo') {
      try { await api.post(`/bookings/${bookingId}/pay-stage`, pay.fields || {}); } catch (e) { /* best-effort */ }
    }
    setLoading(false);
    setResult(pay);
    setStep(3);
  };

  const header = (title) => (
    <div className="topbar-light">
      <button className="muted" onClick={onClose} aria-label="Close" style={{ fontSize: 22 }}>✕</button>
      <div className="topbar-light__title">{title}</div>
      <span style={{ width: 22 }} />
    </div>
  );

  // ---- First screen after Book Now: full package details ----
  if (intro) {
    const svc = serviceChargeFor(pkg, selectedLocation?.name);
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
        {header('Package Details')}
        <div style={{ flex: 1, padding: 18 }}>
          <div className="card row" style={{ marginBottom: 20 }}>
            <div className="grow">
              {pkg.isPopular ? <Badge label="★ Popular" tone="warning" /> : <Badge label={pkg.category} tone="brand" />}
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 8 }}>{pkg.title}</div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{pkg.category}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary)' }}>{rupee(price)}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>/ {pkg.durationType}</div>
            </div>
          </div>

          {svc > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--success)', fontWeight: 700, marginBottom: 12 }}>
              + {rupee(svc)} service charge (incl GST)
            </p>
          )}

          {pkg.beforeBookDescription ? (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>About this service</h3>
              <p className="muted" style={{ lineHeight: 1.6, marginBottom: 8 }}>{pkg.beforeBookDescription}</p>
            </>
          ) : null}

          {(pkg.features || []).length > 0 && (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>What's included</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                {pkg.features.map((f) => <span key={f}>✓&nbsp; {f}</span>)}
              </div>
            </>
          )}

          {(pkg.securityAmount > 0 || pkg.trialDays > 0) && (
            <div className="card card--tight" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {pkg.trialDays > 0 && <span style={{ fontSize: 13.5, fontWeight: 600 }}>🧪&nbsp; {pkg.trialDays}-day trial period</span>}
              {pkg.securityAmount > 0 && <span style={{ fontSize: 13.5, fontWeight: 600 }}>🛡️&nbsp; Refundable security deposit</span>}
              {pkg.shortDescription ? <span className="muted" style={{ fontSize: 13.5 }}>{pkg.shortDescription}</span> : null}
            </div>
          )}
        </div>
        <div style={{ padding: 16, paddingBottom: 'calc(16px + var(--sab))', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          <Button title="Book Now  →" onClick={() => setIntro(false)} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
      {header(step === 3 ? 'Booking Confirmed' : 'Book Service')}

      {step < 3 && (
        <div style={{ display: 'flex', justifyContent: 'space-around', background: 'var(--surface)', paddingBottom: 14 }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
              <span style={{
                width: 26, height: 26, borderRadius: '50%',
                background: i <= step ? 'var(--primary)' : '#EEF0F5',
                color: i <= step ? '#fff' : 'var(--faint)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 12,
              }}>{i + 1}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: i <= step ? 'var(--primary)' : 'var(--faint)' }}>{s}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ flex: 1, padding: 18 }}>
        {step < 3 && (
          <div className="card row" style={{ marginBottom: 20 }}>
            <div className="grow">
              <Badge label={pkg.category} tone="brand" />
              <div style={{ fontSize: 16, fontWeight: 800, marginTop: 8 }}>{pkg.title}</div>
            </div>
            <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary)' }}>{rupee(price)}</span>
          </div>
        )}

        {/* Step 0: Address */}
        {step === 0 && (
          <FadeIn from={12}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Service address</h3>
            <input className="input" style={{ marginBottom: 12 }} placeholder="House / flat no."
              value={addr.house} onChange={(e) => setAddr({ ...addr, house: e.target.value })} />
            <input className="input" style={{ marginBottom: 12 }} placeholder="Building / street / area"
              value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
            <div className="field-row">
              <input className="input" placeholder="City" value={addr.city}
                onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
              <input className="input" placeholder="Pincode" inputMode="numeric" maxLength={6} value={addr.pincode}
                style={{ maxWidth: 140 }}
                onChange={(e) => setAddr({ ...addr, pincode: e.target.value.replace(/\D/g, '') })} />
            </div>
            {selectedLocation && (
              <p className="faint" style={{ fontSize: 12, marginTop: 8 }}>
                📍 City auto-filled from your selected location — you can edit it.
              </p>
            )}
          </FadeIn>
        )}

        {/* Step 1: Schedule */}
        {step === 1 && (
          <FadeIn from={12}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Preferred time slot</h3>
            {autoAssign ? (
              <div className="card" style={{ textAlign: 'center', padding: 22 }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>🕐</div>
                <div style={{ fontWeight: 700 }}>Time slot will be auto-assigned</div>
                <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  Our admin schedules the best available slot for you.
                </p>
              </div>
            ) : (
              slots.map((s) => (
                <button key={s} onClick={() => setSlot(s)} className={`option-row ${slot === s ? 'option-row--on' : ''}`}>
                  <span>{s}</span>
                  <span className={`radio ${slot === s ? 'radio--on' : ''}`} />
                </button>
              ))
            )}
          </FadeIn>
        )}

        {/* Step 2: How it works + pay appointment fee */}
        {step === 2 && (
          <FadeIn from={12}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>How it works</h3>
            {[
              ['📞', 'Schedule appointment', 'Pay the appointment fee to book a consultation call.'],
              ['👥', 'Meet candidates', 'We shortlist workers — you interview them and pick one.'],
              ['🛡️', 'Security & trial', 'Pay the security deposit to start a trial period.'],
              ['🔄', 'Continue', 'Happy after the trial? Pay to continue the service.'],
            ].map(([icon, title, desc], i) => (
              <div key={title} className="row" style={{
                gap: 12, background: 'var(--surface)', borderRadius: 'var(--r-md)', padding: 14, marginBottom: 10,
              }}>
                <span style={{
                  width: 40, height: 40, borderRadius: '50%', background: 'var(--primary-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flex: 'none',
                }}>{icon}</span>
                <div className="grow">
                  <div style={{ fontSize: 14.5, fontWeight: 700 }}>{i + 1}. {title}</div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.4 }}>{desc}</div>
                </div>
              </div>
            ))}

            <div className="row-between" style={{ marginTop: 10, paddingTop: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Appointment fee</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary)' }}>
                {appointmentFee > 0 ? rupee(appointmentFee) : 'Free'}
              </span>
            </div>
            <div className="card card--tight center" style={{ marginTop: 16 }}>
              <span className="muted" style={{ fontSize: 12.5 }}>
                🔒 You only pay the appointment fee now. Security &amp; service fees come later.
              </span>
            </div>
          </FadeIn>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <FadeIn from={16} style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{
              width: 96, height: 96, borderRadius: '50%', background: '#E5F5EE', margin: '0 auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44,
            }}>{result?.success ? '✅' : '⚠️'}</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginTop: 20 }}>
              {result?.success ? 'Appointment Booked!' : 'Payment Failed'}
            </h2>
            <p className="muted" style={{ marginTop: 10, lineHeight: 1.5 }}>
              {result?.success
                ? `We'll call you to schedule your ${pkg.title} consultation and shortlist workers. Track every step under "My Bookings".`
                : result?.error || 'Please try again.'}
            </p>
            {result?.success && result?.paymentId && (
              <div style={{ marginTop: 18, background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: '12px 20px', display: 'inline-block' }}>
                <div className="faint" style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>PAYMENT ID</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3 }}>{result.paymentId}</div>
              </div>
            )}
            {result?.demo && <div style={{ marginTop: 10 }}><Badge label="Demo payment" tone="warning" /></div>}
            <Button title="Done" onClick={onClose} style={{ marginTop: 28, maxWidth: 200, marginLeft: 'auto', marginRight: 'auto' }} />
          </FadeIn>
        )}
      </div>

      {step < 3 && (
        <div className="btn-row" style={{
          padding: 16, paddingBottom: 'calc(16px + var(--sab))',
          background: 'var(--surface)', borderTop: '1px solid var(--border)',
        }}>
          {step > 0 && <Button variant="ghost" title="Back" onClick={() => setStep(step - 1)} />}
          {step < 2 ? (
            <Button title="Continue" onClick={() => canNext && setStep(step + 1)} disabled={!canNext} style={{ flex: 2 }} />
          ) : (
            <Button
              title={loading ? 'Processing…' : appointmentFee > 0 ? `Pay ${rupee(appointmentFee)} & Book` : 'Book Appointment'}
              onClick={placeAndPay} disabled={loading} style={{ flex: 2 }}
            />
          )}
        </div>
      )}

      {loading && (
        <div className="overlay-busy">
          <div className="spinner" />
          <span className="muted">Opening secure checkout…</span>
        </div>
      )}
    </div>
  );
}
